<template>
  <section class="pb-2">
    <e-filters
      ref="filters"
      :title="$t('Filtros')"
      :searching="fetching"
      :search-text="$t('Pesquisar')"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <template #after_buttons>
        <e-button
          id="download"
          class="ml-1 btn-sm-block"
          variant="primary"
          icon="download"
          :busy="fetching"
          :text="$t('Baixar Relatório')"
          @click="downloadReport"
        />
        <!-- Button to download report (hidden by default) -->
        <a
          ref="downloadLink"
          style="display: none"
        />
      </template>
      <div>
        <b-row>
          <b-col md="6">
            <e-store-combo
              id="filters-store"
              v-model="filters.storeId"
              required
              @input="filters.checkingAccountId = null"
            />
          </b-col>

          <b-col md="6">
            <e-checking-account-combo
              v-model="filters.checkingAccountId"
              :type="['InternetBanking']"
              :store-id="filters.storeId"
              required
              active-options
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <FormulateInput
              id="period"
              v-model="filters.period"
              type="date-range-picker"
              :label="$t('Período')"
              :time-picker="false"
              validation="requiredrange"
              :validation-messages="{
                requiredrange: $t('Informe um período'),
              }"
              class="required"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              v-model="filters.initialValue"
              type="text-number"
              currency="R$"
              :precision="2"
              :label="$t('Valor inicial')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              v-model="filters.finalValue"
              type="text-number"
              currency="R$"
              :precision="2"
              :label="$t('Valor final')"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          :per-page-options="[10, 20, 50]"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        ref="ddalist-table"
        show-empty
        striped
        responsive
        :class="{
          bordered: true,
          'mb-1': true,
          'bill-pay-select-row': true,
        }"
        :fields="ddaFields"
        :items="paginatedItems"
        :empty-text="getEmptyTableMessage($tc('DDA_BANK_SLIP.NAME'), 'male')"
      >
        <template #cell(bulkPay)="row">
          <FormulateInput
            v-model="installmentPayForm.bankSlipDdaCode"
            type="radio"
            :element-class="['d-flex', 'mt-0']"
            name="dda-select"
            :value="row.item.code"
          />
        </template>
      </b-table>
      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="ddaList.length"
            :total-on-page="paginatedItems.length"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="ddaList.length"
            :per-page="paging.pageSize"
            aria-controls="checking-account-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>
  </section>
</template>

<script>
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EPageSizeSelector,
  EPaginationSummary,
  EStoreCombo,
  EButton,
  EFilters,
  ECheckingAccountCombo,
} from '@/views/components'
import { alerts, financialDomain } from '@/mixins'
import Decimal from 'decimal.js-light'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    EStoreCombo,
    EFilters,
    EButton,
    ECheckingAccountCombo,
  },

  mixins: [alerts, financialDomain],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
    }
  },

  computed: {
    ...mapState('pages/financial/internetBankingDda', [
      'ddaList',
      'reportData',
      'paging',
      'sorting',
      'filters',
      'summaryFields',
    ]),

    ddaFields() {
      return [
        {
          label: '',
          key: 'bulkPay',
          tdClass: 'pl-2 justify-content-center align-items-center',
          thStyle: { width: '50px' },
          hide: true,
        },
        {
          label: this.$t('Código de barras'),
          key: 'code',
          sortKey: 'code',
          tdClass: 'text-center',
          sortable: true,
        },
        {
          label: this.$t('Venc.'),
          key: 'dueDate',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          formatter: val => this.$options.filters.date(val),
          sortable: true,
        },
        {
          label: this.$t('Fornecedor'),
          key: 'beneficiary',
          formatter: val =>
            `${this.$options.filters.cpfCnpj(val.beneficiaryDocument)} - ${val.beneficiaryName}`,
        },
        {
          label: this.$t('Pagador'),
          key: 'payerDocumentNumber',
          formatter: val =>
            this.$options.filters.cpfCnpj(val),
        },
        {
          label: this.$t('Valor'),
          key: 'nominalValue',
          tdClass: 'text-right',
          thStyle: { minWidth: '110px', width: '110px' },
          formatter: val => this.$options.filters.currency(new Decimal(val.replace(',', '.'))),
          sortable: true,
        },
      ].filter(field => !field.hide)
    },

    paginatedItems() {
      const start = (this.paging.currentPage - 1) * this.paging.pageSize
      const end = start + this.paging.pageSize
      return this.ddaList.slice(start, end)
    },
  },

  mounted() {},

  methods: {
    ...mapActions('pages/financial/internetBankingDda', [
      'fetchDdaFind',
      'generateReport',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),
    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchDdaFind()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },
    filterData() {
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      // this.getData()
    },

    async downloadReport() {
      try {
        if (this.$refs.filters.isValid()) {
          this.fetching = true
          await this.generateReport()
          // Get the download link element from the template
          const { downloadLink } = this.$refs

          if (this.reportData) {
            // Set the URL and download attributes of the link
            downloadLink.href = this.reportData
            downloadLink.download = `Extrato_Dda_${this.filters.storeId}_IdConta_${
              this.filters.checkingAccountId
            }_${moment().format()}.xlsx`

            // Click the link to start the download
            downloadLink.click()
          } else {
            this.showError({ message: this.$t('Não foi possível recuperar os dados do relatório') })
          }
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },

    onRowClickToSelectPay() {},
  },
}
</script>

<style></style>
